import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Phone } from 'lucide-react'

import { track } from '@/api/tracking'
import { LeadForm, type LeadFormSuccessResult } from '@/components/LeadForm'
import { LeadFormOfferSuccess } from '@/components/LeadFormOfferSuccess'
import { useSectionViewTrack } from '@/hooks/useSectionViewTrack'
import type { LandingPage } from '@tipi/shared'

import { FOOD_COST_LANDING_PAGE, FORM_HASH, LANDING_DIRECT_CONVERSION_EXPERIMENT } from './constants'

const FORM_FIELD_CLASS =
    'h-[54px] min-w-0 w-full rounded-t-[6px] rounded-b-none border-0 border-b-2 border-[#241A12] bg-[rgba(36,26,18,0.08)] px-4 text-[15px] font-medium leading-none text-[#5C5548] shadow-none placeholder:text-[#8A7F6F] focus-visible:bg-[rgba(240,79,33,0.1)] focus-visible:border-b-[#F04F21] focus-visible:ring-0'

interface FinalCtaSectionProps {
    isDirectConversion?: boolean
    landingPage?: LandingPage
    formTitle?: string
    formSubtitle?: string
}

const LAST_CHANCE_MIXPANEL_CONTEXT = {
    experiment: LANDING_DIRECT_CONVERSION_EXPERIMENT,
    placement: 'last_chance',
} as const

function PostLeadOfferSection({
    offer,
    landingPage,
    mixpanelContext,
}: {
    offer: LeadFormSuccessResult
    landingPage: LandingPage
    mixpanelContext?: Record<string, unknown>
}) {
    return (
        <section className="bg-[#F5EEE4] pb-8 pt-2 font-['Archivo',system-ui,sans-serif] md:pb-12">
            <div id='form' className='scroll-mt-24'>
                <LeadFormOfferSuccess
                    leadId={offer.leadId}
                    createdAt={offer.createdAt}
                    landingPage={landingPage}
                    mixpanelContext={mixpanelContext}
                />
            </div>
        </section>
    )
}

function LastChanceCollapsedForm({ landingPage }: { landingPage: LandingPage }) {
    const { t } = useTranslation()
    const sectionRef = useRef<HTMLDivElement>(null)
    const hasTrackedLastChanceOpen = useRef(false)
    const [isFormOpen, setIsFormOpen] = useState(() => window.location.hash === FORM_HASH)
    const [offerSuccess, setOfferSuccess] = useState<LeadFormSuccessResult | null>(null)
    // Treatment Form = last-chance CTA in view (not full lead form). Filter with placement.
    useSectionViewTrack(sectionRef, 'Form', { landing_page: landingPage, ...LAST_CHANCE_MIXPANEL_CONTEXT })

    const trackLastChanceOpened = () => {
        if (hasTrackedLastChanceOpen.current) return
        hasTrackedLastChanceOpen.current = true
        track('Last Chance Form Opened', {
            landing_page: landingPage,
            experiment: LANDING_DIRECT_CONVERSION_EXPERIMENT,
        })
    }

    useEffect(() => {
        const openFromHash = () => {
            if (window.location.hash !== FORM_HASH) return
            setIsFormOpen(true)
            trackLastChanceOpened()
        }

        openFromHash()
        window.addEventListener('hashchange', openFromHash)
        return () => window.removeEventListener('hashchange', openFromHash)
    }, [landingPage])

    const openForm = () => {
        setIsFormOpen(true)
        trackLastChanceOpened()
        if (window.location.hash !== FORM_HASH) {
            window.history.replaceState(null, '', FORM_HASH)
        }
        requestAnimationFrame(() => {
            sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        })
    }

    if (offerSuccess) {
        return (
            <PostLeadOfferSection
                offer={offerSuccess}
                landingPage={landingPage}
                mixpanelContext={LAST_CHANCE_MIXPANEL_CONTEXT}
            />
        )
    }

    return (
        <section className="bg-[#F5EEE4] pb-8 pt-2 font-['Archivo',system-ui,sans-serif] md:pb-12">
            <div
                id='form'
                ref={sectionRef}
                className='mx-auto flex max-w-[520px] scroll-mt-24 flex-col items-center gap-3 px-[22px] py-6 text-center md:px-10'
            >
                {!isFormOpen ? (
                    <button
                        type='button'
                        onClick={openForm}
                        className='inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-[#C9C0B3] bg-transparent px-5 py-2.5 text-sm font-semibold text-[#5C5548] transition-colors hover:border-[#8A7F6F] hover:bg-white/50'
                    >
                        <Phone className='size-4 shrink-0' aria-hidden />
                        {t('foodCost.finalCta.lastChance.openButton', 'Prefiero que me llaméis')}
                    </button>
                ) : (
                    <div className='w-full text-left text-[#241A12]'>
                        <div
                            className='h-3 bg-[linear-gradient(-45deg,#f4eee3_6px,transparent_0),linear-gradient(45deg,#f4eee3_6px,transparent_0)] bg-[length:12px_12px] bg-repeat-x [background-position:bottom]'
                            aria-hidden='true'
                        />
                        <div className='border border-[#E2D9CB] bg-[#f4eee3] px-5 pb-5 pt-5 md:px-6'>
                            <p className='m-0 mb-4 text-center text-[13px] font-semibold leading-snug text-[#5C5548]'>
                                {t('foodCost.finalCta.lastChance.receiptHighlight', 'Déjanos tu móvil y te atendemos.')}
                            </p>
                            <div className='[&_[data-slot=checkbox-indicator]]:text-[#f4eee3] [&_[data-slot=checkbox][data-state=checked]]:border-[#241A12] [&_[data-slot=checkbox][data-state=checked]]:bg-[#241A12] [&_[data-slot=checkbox]]:mt-0 [&_[data-slot=checkbox]]:size-6 [&_[data-slot=checkbox]]:rounded-[4px] [&_[data-slot=checkbox]]:border-[#8E8476] [&_[data-slot=checkbox]]:bg-transparent [&_a]:font-bold [&_a]:text-[#C54A2C] [&_a]:underline [&_form]:space-y-5 [&_label]:text-[#241A12] [&_p]:text-[#5C5548]'>
                                <LeadForm
                                    landingPage={landingPage}
                                    mixpanelContext={LAST_CHANCE_MIXPANEL_CONTEXT}
                                    showIntroOfferOnSuccess={false}
                                    onSuccess={result => {
                                        if (result.createdAt) setOfferSuccess(result)
                                    }}
                                    emphasizePrimaryCta
                                    defaultCallIntent='schedule'
                                    submitButtonText={t(
                                        'foodCost.finalCta.lastChance.submitButton',
                                        'Prefiero que me llaméis',
                                    )}
                                    submitButtonIcon={<Phone className='size-[18px] shrink-0' aria-hidden />}
                                    fieldClassName={FORM_FIELD_CLASS}
                                    nameFieldLabel={t('foodCost.finalCta.form.nameFieldLabel', '01 · Nombre')}
                                    phoneFieldLabel={t('foodCost.finalCta.form.phoneFieldLabel', '02 · Móvil')}
                                    roleFieldLabel={t('foodCost.finalCta.form.roleFieldLabel', '03 · Rol')}
                                    fieldLabelClassName='text-[rgba(36,26,18,0.65)]'
                                    consentSectionClassName='pt-4 opacity-100'
                                    consentLabelClassName='text-[13px] leading-[1.7] text-[#241A12]'
                                    consentSubtextClassName='text-[11px] leading-[1.8] text-[#5C5548]'
                                    submitButtonClassName='flex h-auto min-h-[48px] items-center justify-center gap-2 whitespace-normal text-balance rounded-full border border-[#241A12] bg-transparent px-4 py-3 text-[14px] font-bold text-[#241A12] shadow-none hover:bg-[rgba(36,26,18,0.06)]'
                                    formErrorClassName='text-[#C54A2C]'
                                />
                            </div>
                        </div>
                        <div
                            className='h-3 bg-[linear-gradient(-45deg,#f4eee3_6px,transparent_0),linear-gradient(45deg,#f4eee3_6px,transparent_0)] bg-[length:12px_12px] bg-repeat-x [background-position:bottom] [transform:scaleY(-1)]'
                            aria-hidden='true'
                        />
                    </div>
                )}
            </div>
        </section>
    )
}

export function FinalCtaSection({
    isDirectConversion = false,
    landingPage = FOOD_COST_LANDING_PAGE,
    formTitle,
    formSubtitle,
}: FinalCtaSectionProps) {
    const { t } = useTranslation()
    const formRef = useRef<HTMLDivElement>(null)

    const [offerSuccess, setOfferSuccess] = useState<LeadFormSuccessResult | null>(null)

    useSectionViewTrack(formRef, 'Form', { landing_page: landingPage }, !isDirectConversion)

    if (isDirectConversion) {
        return <LastChanceCollapsedForm landingPage={landingPage} />
    }

    if (offerSuccess) {
        return <PostLeadOfferSection offer={offerSuccess} landingPage={landingPage} />
    }

    return (
        <section className="bg-[#F5EEE4] pb-3 pt-1 font-['Space_Mono',monospace] md:pb-6">
            <div
                id='form'
                ref={formRef}
                className='mx-auto flex max-w-[1180px] scroll-mt-20 flex-col gap-4 px-3 pt-8 md:gap-5 md:px-6 md:pt-12'
            >
                <div className='grid grid-cols-1 gap-8 rounded-[28px] bg-[#17100A] px-4 pb-4 pt-4 md:grid-cols-[minmax(0,0.88fr)_minmax(390px,0.82fr)] md:px-6 md:py-6'>
                    <div className='flex flex-col justify-between gap-6 px-2 pt-2 md:pr-4'>
                        <div className='flex flex-col gap-4'>
                            <h2 className="m-0 max-w-[18ch] font-['Archivo',system-ui,sans-serif] text-[34px] font-bold leading-[1.12] tracking-[-1px] text-[#F5EEE4] md:text-[52px] md:tracking-[-2px]">
                                {formTitle ??
                                    t(
                                        'foodCost.finalCta.form.title',
                                        'Ten Tipi listo para empezar en menos de 48 horas',
                                    )}
                            </h2>
                            <p className="m-0 max-w-[34ch] font-['Archivo',system-ui,sans-serif] text-[15px] leading-[1.7] text-[#C9C0B3] md:text-[17px]">
                                {formSubtitle ??
                                    t(
                                        'foodCost.finalCta.form.subtitle',
                                        'Con tu último mes analizado y un objetivo para mejorar tu negocio. Déjanos tus datos y te llamamos.',
                                    )}
                            </p>
                        </div>

                        <div className="hidden font-['Archivo',system-ui,sans-serif] md:flex md:flex-col md:gap-3 md:text-sm md:text-[#C9C0B3]">
                            <div>
                                {t('foodCost.finalCta.form.bullet1', 'Con tu carta y tus proveedores ya cargados.')}
                            </div>
                            <div>
                                {t(
                                    'foodCost.finalCta.form.bullet2',
                                    'Sin compromiso. Te contactamos después de enviarlo.',
                                )}
                            </div>
                        </div>
                    </div>

                    <div className='text-[#241A12]' style={{ filter: 'drop-shadow(0 12px 24px rgba(0,0,0,.5))' }}>
                        <div
                            className='h-3 bg-[linear-gradient(-45deg,#f4eee3_6px,transparent_0),linear-gradient(45deg,#f4eee3_6px,transparent_0)] bg-[length:12px_12px] bg-repeat-x [background-position:bottom]'
                            aria-hidden='true'
                        />

                        <div className='bg-[#f4eee3] px-5 pb-5 pt-6 md:px-6 md:pb-6'>
                            <div className='border-b-0 border-t-0 pb-4 text-center'>
                                <div className='text-[12px] font-bold uppercase tracking-[0.3em] text-[#241A12]'>
                                    {t('foodCost.finalCta.form.receiptBrand', '* TIPI *')}
                                </div>
                                <div className='mt-2 text-[11px] font-medium uppercase tracking-[0.18em] text-[rgba(36,26,18,0.45)]'>
                                    {t('foodCost.finalCta.form.receiptSubtitle', 'TEN TIPI EN 48 H')}
                                </div>
                            </div>

                            <div className='border-t-2 border-dashed border-t-[rgba(36,26,18,0.3)] py-4'>
                                <div className='text-sm font-semibold leading-relaxed text-[#241A12] md:text-[15px]'>
                                    {t('foodCost.finalCta.form.receiptHighlightPrefix', 'Te llamamos y en menos de')}{' '}
                                    <span className='bg-[#F7D9CC] px-1.5 py-0.5 font-bold text-[#17130E]'>
                                        {t('foodCost.finalCta.form.receiptHighlight', '48 horas')}
                                    </span>{' '}
                                    {t(
                                        'foodCost.finalCta.form.receiptHighlightSuffix',
                                        'lo tienes listo, con tu último mes analizado y un objetivo de mejora',
                                    )}
                                </div>
                            </div>

                            <div className='border-t-2 border-dashed border-t-[rgba(36,26,18,0.3)] pt-4 [&_[data-slot=checkbox-indicator]]:text-[#f4eee3] [&_[data-slot=checkbox][data-state=checked]]:border-[#241A12] [&_[data-slot=checkbox][data-state=checked]]:bg-[#241A12] [&_[data-slot=checkbox]]:mt-0 [&_[data-slot=checkbox]]:size-6 [&_[data-slot=checkbox]]:rounded-[4px] [&_[data-slot=checkbox]]:border-[#8E8476] [&_[data-slot=checkbox]]:bg-transparent [&_a]:font-bold [&_a]:text-[#C54A2C] [&_a]:underline [&_form]:space-y-5 [&_label]:text-[#241A12] [&_p]:text-[#5C5548]'>
                                <LeadForm
                                    landingPage={landingPage}
                                    showIntroOfferOnSuccess={false}
                                    onSuccess={result => {
                                        if (result.createdAt) setOfferSuccess(result)
                                    }}
                                    emphasizePrimaryCta
                                    defaultCallIntent='schedule'
                                    submitButtonText={t(
                                        'foodCost.finalCta.form.submitButton',
                                        'Quiero Tipi en 48 horas',
                                    )}
                                    fieldClassName={FORM_FIELD_CLASS}
                                    nameFieldLabel={t('foodCost.finalCta.form.nameFieldLabel', '01 · Nombre')}
                                    phoneFieldLabel={t('foodCost.finalCta.form.phoneFieldLabel', '02 · Móvil')}
                                    roleFieldLabel={t('foodCost.finalCta.form.roleFieldLabel', '03 · Rol')}
                                    fieldLabelClassName='text-[rgba(36,26,18,0.65)]'
                                    consentSectionClassName='pt-4 opacity-100'
                                    consentLabelClassName='text-[13px] leading-[1.7] text-[#241A12]'
                                    consentSubtextClassName='text-[11px] leading-[1.8] text-[#5C5548]'
                                    submitButtonClassName='flex h-auto min-h-[54px] items-center justify-center gap-2 whitespace-normal text-balance px-4 py-[18px] leading-tight rounded-[12px] border-none bg-[#D13D1A] text-[15px] font-bold uppercase tracking-[0.12em] text-white shadow-none hover:bg-[#C03214]'
                                    formErrorClassName='text-[#C54A2C]'
                                />
                            </div>
                        </div>

                        <div
                            className='h-3 bg-[linear-gradient(-45deg,#f4eee3_6px,transparent_0),linear-gradient(45deg,#f4eee3_6px,transparent_0)] bg-[length:12px_12px] bg-repeat-x [background-position:bottom] [transform:scaleY(-1)]'
                            aria-hidden='true'
                        />
                    </div>
                </div>
            </div>
        </section>
    )
}
